import React from "react";
import moment from "moment";
import {DateRange} from "../common/dtos";
import {EventDto} from "../event-details/dtos";
import {Table} from "antd";
import {ColumnProps} from "antd/es/table";
import './Calendar.css';
import EmptyView from "./view/EmptyView";
import {indexRowKeyFn} from "../utils/table-utils";
import DayView from "./view/DayView";
import TimeView from "./view/TimeView";
import {
  calendarCellHeight,
  calendarMinDayColumnWidth,
  calendarTableWidth,
  calendarTimeColumnWidth, calendarMinuteStep,
  DailyTimeSlots,
  generateDailyTimeSlots
} from "./calendar-utils";
import EventView from "./view/EventView";


interface CalendarProps {
  dateRange: DateRange;
  events: EventDto[];
  onEditEventClick: (event: EventDto) => void;
  onDeleteEventClick: (event: EventDto) => void;
  onTimeRangeClick: (timeRange: DateRange) => void;
}

interface State {
  windowHeight: number;
}

const padding = 24;
const margin = 24;

class Calendar extends React.Component<CalendarProps, State> {
  constructor(props: CalendarProps) {
    super(props);
    this.state = {
      windowHeight: window.innerHeight,
    }
  }


  public componentDidMount() {
    const tableBody = document.querySelector('.ant-table-body');
    if (tableBody) {
      // Scrolls vertically to 6:00 AM by default
      tableBody.scrollTop = calendarCellHeight * (60 / calendarMinuteStep) * 6;
      // Scrolls horizontally to the current day column
      tableBody.scrollLeft = calendarMinDayColumnWidth * (moment().day() - 1);
    }

    window.addEventListener('resize', this.updateHeight);

  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.updateHeight);
  }

  public render(): React.ReactNode {

    const {start} = this.props.dateRange;
    const availableHeight = this.calculateCalendarHeight();

    const dailyTimeSlots: DailyTimeSlots[] = generateDailyTimeSlots(start);

    const columns: Array<ColumnProps<DailyTimeSlots>> = [
      this.timeColumn(),
      this.dayColumn(0),
      this.dayColumn(1),
      this.dayColumn(2),
      this.dayColumn(3),
      this.dayColumn(4),
      this.dayColumn(5),
      this.dayColumn(6),
    ];

    return (
      <Table<DailyTimeSlots>
        className="calendar"
        size="small"
        rowKey={indexRowKeyFn}
        columns={columns}
        dataSource={dailyTimeSlots}
        bordered={true}
        showHeader={true}
        pagination={false}
        scroll={{x: calendarTableWidth, y: availableHeight}}
      />
    );
  }

  private timeColumn = (): ColumnProps<DailyTimeSlots> => {
    return {
      key: 'time',
      fixed: "left",
      className: "time-slot-cell",
      width: calendarTimeColumnWidth,
      render: (_, record) => {
        return (
          <TimeView time={record.days[0]}/>
        );
      }
    }
  }

  private dayColumn = (dayIndex: number): ColumnProps<DailyTimeSlots> => {
    const {dateRange, onTimeRangeClick, onEditEventClick, onDeleteEventClick} = this.props;
    const day = dateRange.start.clone().add(dayIndex, 'days');

    return {
      key: `day-${dayIndex}`,
      className: "day-time-slot-cell",
      title: () => {
        return (
          <DayView day={day}/>
        );
      },
      render: (_, record) => {
        const time = record.days[dayIndex]
        const event = this.findEventAtTime(time);

        if (event) {
          return (
            <EventView event={event} onEditClick={onEditEventClick} onDeleteClick={onDeleteEventClick}/>
          );
        } else {
          return (
            <EmptyView time={time} onClick={onTimeRangeClick}/>
          );
        }
      }
    };
  };

  private findEventAtTime = (start: moment.Moment): EventDto | undefined => {
    const {events} = this.props;
    return events.find((e) => {
      return e.start.isSame(start);
    });
  }

  private updateHeight = () => {
    this.setState({
      windowHeight: window.innerHeight
    })
  }

  private calculateCalendarHeight = (): number => {
    const {windowHeight} = this.state;

    const pageHeaderHeight = (document.querySelector('.ant-page-header')?.clientHeight ?? 0) + 1;
    const calendarHeaderHeight = (document?.querySelector('.calendar-header')?.clientHeight ?? 0) + 1;
    const pageFooterHeight = document?.querySelector('.app-footer')?.clientHeight ?? 0;
    const tableHeader = document?.querySelector('.ant-table-header')?.clientHeight ?? 0;

    return windowHeight - (pageHeaderHeight + calendarHeaderHeight + pageFooterHeight + tableHeader + 2 * padding + margin)
  }
}

export default Calendar;


// WEBPACK FOOTER //
// ./src/event/Calendar.tsx